// custom-ui/HomeActionCard.tsx
// A "next best action" card for the guided Home surfaces. Leading IconChip,
// title + description, and a CTA. `primary` renders the hero (top) action with a
// sage accent + solid button; others are quieter with an outline button.
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconChip } from './IconChip';
import { ActionButton, OutlineButton } from './action-button';

type Tone = 'brand' | 'neutral' | 'green' | 'amber' | 'red' | 'blue';

interface HomeActionCardProps {
    icon?: LucideIcon;
    // Optional custom leading visual (e.g. <LinkAvatar/>) — overrides `icon`.
    iconNode?: React.ReactNode;
    tone?: Tone;
    title: string;
    description?: string;
    ctaLabel: string;
    ctaIcon?: LucideIcon;
    onClick: () => void;
    primary?: boolean;
}

export const HomeActionCard: React.FC<HomeActionCardProps> = ({
    icon, iconNode, tone = 'brand', title, description, ctaLabel, ctaIcon, onClick, primary = false,
}) => {
    const Btn = primary ? ActionButton : OutlineButton;
    return (
        <div
            className={cn(
                'rounded-2xl border transition-all duration-200 p-5 flex items-center gap-4',
                primary
                    ? 'bg-[#A4C3B2]/15 border-[#A4C3B2]/50 shadow-sm'
                    : 'bg-white border-gray-100 shadow-sm hover:shadow-md'
            )}
        >
            {iconNode
                ? <div className="flex-shrink-0">{iconNode}</div>
                : icon && <IconChip icon={icon} size="lg" tone={tone} />}
            <div className="min-w-0 flex-1">
                <h3 className={cn('font-semibold text-gray-900', primary ? 'text-lg' : 'text-base')}>{title}</h3>
                {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
            </div>
            <div className="flex-shrink-0">
                <Btn icon={ctaIcon} onClick={onClick}>{ctaLabel}</Btn>
            </div>
        </div>
    );
};

export default HomeActionCard;
